import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import CustomerDropdown from './CustomerDropdown';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  UserIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  XCircleIcon,
  ShoppingCartIcon,
  TruckIcon,
  CheckIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';

const OrderForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEditMode = Boolean(id);
  const queryParams = new URLSearchParams(location.search);
  const orderType = queryParams.get('type') || 'customers'; // 'customers' ou 'suppliers'
  
  const { 
    addOrder, 
    updateOrder, 
    getOrderById, 
    getProducts, 
    getCustomers, 
    getSuppliers, 
    fetchAllData 
  } = useAppData();
  
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showProductSearch, setShowProductSearch] = useState(false);
  // State for product search only
  const [isSearching, setIsSearching] = useState(false);
  
  // État initial pour les commandes clients
  const customerOrderInitialState = {
    orderNumber: '',
    date: new Date().toISOString().split('T')[0],
    customerId: '',
    customerName: '',
    reference: '',
    documentType: 'bon-commande',
    paymentMethod: 'virement',
    paymentStatus: 'pending',
    status: 'en-attente',
    dueDate: '',
    notes: '',
    items: []
  };
  
  // État initial pour les commandes fournisseurs
  const supplierOrderInitialState = {
    orderNumber: '',
    date: new Date().toISOString().split('T')[0],
    supplierId: '',
    supplierName: '',
    reference: '',
    documentType: 'bon-commande',
    status: 'en-attente',
    deliveryStatus: 'non-expedie',
    expectedDate: '',
    notes: '',
    items: []
  };
  
  const [orderData, setOrderData] = useState(
    orderType === 'customers' ? customerOrderInitialState : supplierOrderInitialState
  );
  
  // Données pour les clients, fournisseurs et produits depuis le contexte
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  
  // Charger les données des clients, fournisseurs et produits en temps réel
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load all data from localStorage
        const customersData = getCustomers();
        const suppliersData = getSuppliers();
        const productsData = getProducts();
        
        console.log('Loaded customers:', customersData.length);
        console.log('Loaded suppliers:', suppliersData.length);
        console.log('Loaded products:', productsData.length);
        
        // Sort customers and suppliers alphabetically by name
        const sortedCustomers = [...customersData].sort((a, b) => {
          const nameA = getEntityName(a).toLowerCase();
          const nameB = getEntityName(b).toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        const sortedSuppliers = [...suppliersData].sort((a, b) => {
          const nameA = getEntityName(a).toLowerCase();
          const nameB = getEntityName(b).toLowerCase();
          return nameA.localeCompare(nameB);
        });
        
        setCustomers(sortedCustomers);
        setSuppliers(sortedSuppliers);
        setProducts(productsData);
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Erreur lors du chargement des données');
      }
    };
    
    loadData();
    
    // If location state has customerId or supplierId, pre-select that entity
    if (location.state) {
      if (location.state.customerId && orderType === 'customers') {
        const customer = getCustomers().find(c => c.id === location.state.customerId);
        if (customer) {
          setOrderData(prev => ({
            ...prev,
            customerId: customer.id,
            customerName: getEntityName(customer)
          }));
          toast.success(`Client ${getEntityName(customer)} sélectionné`);
        }
      } else if (location.state.supplierId && orderType === 'suppliers') {
        const supplier = getSuppliers().find(s => s.id === location.state.supplierId);
        if (supplier) {
          setOrderData(prev => ({
            ...prev,
            supplierId: supplier.id,
            supplierName: getEntityName(supplier)
          }));
          toast.success(`Fournisseur ${getEntityName(supplier)} sélectionné`);
        }
      }
    }
  }, [getCustomers, getSuppliers, getProducts, location.state, orderType]);

  useEffect(() => {
    // Générer un numéro de commande automatique
    if (!isEditMode) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const prefix = orderType === 'customers' ? 'CMD-C' : 'CMD-F';
      const orderNumber = `${prefix}-${year}-${month}-${Math.floor(Math.random() * 900) + 100}`;
      
      setOrderData(prev => ({
        ...prev,
        orderNumber
      }));
    }
  }, [isEditMode, orderType]);

  useEffect(() => {
    // Si en mode édition, charger les données de la commande
    if (isEditMode) {
      setIsLoading(true);
      
      try {
        // Récupérer la commande par ID
        const existingOrder = getOrderById(id);
        
        if (existingOrder) {
          setOrderData(existingOrder);
        } else {
          toast.error('Commande introuvable');
          navigate('/orders');
        }
      } catch (error) {
        console.error('Erreur lors du chargement de la commande:', error);
        toast.error('Erreur lors du chargement de la commande');
        navigate('/orders');
      } finally {
        setIsLoading(false);
      }
    }
  }, [isEditMode, id, orderType, getOrderById, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setOrderData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddItem = () => {
    setShowProductSearch(true);
  };

  const handleSelectProduct = (product) => {
    const newItem = {
      id: product.id,
      name: product.name,
      sku: product.sku,
      quantity: 1,
      price: Number(product.price ?? 0),
      total: Number(product.price ?? 0),
      unit: product.unit
    };
    
    setOrderData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    
    setShowProductSearch(false);
    setSearchQuery('');
  };

  const handleRemoveItem = (index) => {
    setOrderData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...orderData.items];
    
    if (field === 'quantity' || field === 'price') {
      const numericValue = Number(value);
      updatedItems[index][field] = isNaN(numericValue) ? 0 : numericValue;
      const q = Number(updatedItems[index].quantity ?? 0);
      const p = Number(updatedItems[index].price ?? 0);
      updatedItems[index].total = (isNaN(q) ? 0 : q) * (isNaN(p) ? 0 : p);
    } else {
      updatedItems[index][field] = value;
    }
    
    setOrderData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const handleSelectEntity = (entity) => {
    if (orderType === 'customers') {
      setOrderData(prev => ({
        ...prev,
        customerId: entity.id,
        customerName: getEntityName(entity)
      }));
      
      // Fetch customer's past orders and populate suggestions
      try {
        // Try to load sales data related to this customer
        const allOrders = localStorage.getItem('stockpro_orders') || localStorage.getItem('orders');
        const salesData = [];
        
        if (allOrders) {
          const parsedOrders = JSON.parse(allOrders);
          const customerOrders = parsedOrders.filter(order => order.customerId === entity.id)
            .sort((a, b) => new Date(b.date) - new Date(a.date));
          
          if (customerOrders.length > 0) {
            // If customer has past orders, show a success toast
            toast.success(`${customerOrders.length} commande(s) passée(s) trouvée(s) pour ce client`);
            
            // Get the most recent order to suggest payment method
            const recentOrder = customerOrders[0];
            if (recentOrder.paymentMethod && !orderData.paymentMethod) {
              setOrderData(prev => ({
                ...prev,
                paymentMethod: recentOrder.paymentMethod
              }));
              
              toast.success(`Méthode de paiement suggérée: ${
                recentOrder.paymentMethod === 'especes' ? 'Espèces' : 
                recentOrder.paymentMethod === 'cheque' ? 'Chèque' :
                recentOrder.paymentMethod === 'virement' ? 'Virement bancaire' :
                'Carte bancaire'
              }`);
            }
          } else {
            toast.info("Aucune commande passée pour ce client");
          }
        }
      } catch (error) {
        console.error("Erreur lors du chargement de l'historique client:", error);
      }
      
    } else {
      setOrderData(prev => ({
        ...prev,
        supplierId: entity.id,
        supplierName: getEntityName(entity)
      }));
      
      // Fetch supplier's past orders
      try {
        // Try to load purchase data related to this supplier
        const allPurchases = localStorage.getItem('stockpro_purchases') || localStorage.getItem('purchases');
        
        if (allPurchases) {
          const parsedPurchases = JSON.parse(allPurchases);
          const supplierPurchases = parsedPurchases.filter(purchase => 
            purchase.supplierId === entity.id || purchase.supplierName === getEntityName(entity)
          ).sort((a, b) => new Date(b.date) - new Date(a.date));
          
          if (supplierPurchases.length > 0) {
            // If supplier has past purchases, show a success toast
            toast.success(`${supplierPurchases.length} achat(s) précédent(s) avec ce fournisseur`);
            
            // Suggest commonly ordered products
            const frequentItems = supplierPurchases
              .flatMap(purchase => purchase.items || [])
              .reduce((acc, item) => {
                const existingItem = acc.find(i => i.id === item.id);
                if (existingItem) {
                  existingItem.count += 1;
                } else {
                  acc.push({...item, count: 1});
                }
                return acc;
              }, [])
              .sort((a, b) => b.count - a.count)
              .slice(0, 3);
            
            if (frequentItems.length > 0) {
              toast.success("Articles fréquemment commandés disponibles", {
                duration: 5000,
              });
            }
          } else {
            toast.info("Aucun achat précédent avec ce fournisseur");
          }
        }
      } catch (error) {
        console.error("Erreur lors du chargement de l'historique fournisseur:", error);
      }
    }
    
    // Dropdown is closed by the CustomerDropdown component
  };

  // Fonction utilitaire pour obtenir le nom complet d'une entité (client ou fournisseur)
  const getEntityName = (ent) => {
    if (ent.name) return ent.name;
    const first = ent.firstName || '';
    const last = ent.lastName || '';
    return `${first} ${last}`.trim();
  };

  const filteredProducts = searchQuery.trim() === '' 
    ? products.slice(0, 50) // Show the first 50 products when no search is performed
    : products.filter(product => 
        (product.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (product.sku || '').toLowerCase().includes(searchQuery.toLowerCase())
      );

  // Filtering of entities is now handled by the CustomerDropdown component

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation basique
    if (orderType === 'customers' && !orderData.customerId) {
      toast.error('Veuillez sélectionner un client');
      return;
    }
    
    if (orderType === 'suppliers' && !orderData.supplierId) {
      toast.error('Veuillez sélectionner un fournisseur');
      return;
    }
    
    if (orderData.items.length === 0) {
      toast.error('Veuillez ajouter au moins un article à la commande');
      return;
    }
    
    setIsLoading(true);
    
    try {
      if (isEditMode) {
        // Mise à jour d'une commande existante
        await updateOrder(orderData.id, orderData);
        toast.success(`La commande ${orderData.orderNumber} a été mise à jour`);
      } else {
        // Création d'une nouvelle commande
        await addOrder(orderData);
        toast.success(`La commande ${orderData.orderNumber} a été créée avec succès`);
      }
      
      // Rafraîchir les données pour s'assurer que l'UI est à jour
      await fetchAllData();
      
      // Navigation vers la liste des commandes
      navigate('/orders');
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de la commande:', error);
      toast.error('Erreur lors de l\'enregistrement de la commande');
    } finally {
      setIsLoading(false);
    }
  };

  // Calculer le total de la commande (robuste aux valeurs string/NaN)
  const totalAmount = orderData.items.reduce((sum, item) => {
    const quantity = Number(item.quantity ?? 0);
    const price = Number(item.price ?? 0);
    const lineTotal = Number(item.total ?? (quantity * price));
    return sum + (isNaN(lineTotal) ? 0 : lineTotal);
  }, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/orders')}
            className="p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-6 w-6 text-white" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">
              {isEditMode 
                ? `Modifier la commande ${orderType === 'customers' ? 'client' : 'fournisseur'}`
                : `Nouvelle commande ${orderType === 'customers' ? 'client' : 'fournisseur'}`}
            </h1>
            <p className="text-white/60 mt-1">
              {isEditMode 
                ? `Modifier les informations de la commande ${orderData.orderNumber}`
                : `Créez une nouvelle commande ${orderType === 'customers' ? 'client' : 'fournisseur'}`}
            </p>
          </div>
        </div>
      </div>

      {/* Formulaire */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-white"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Informations d'en-tête */}
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <h2 className="text-xl font-semibold text-white mb-6">Informations de la commande</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="orderNumber" className="block text-white font-medium mb-1">
                    Numéro de commande
                  </label>
                  <input
                    type="text"
                    id="orderNumber"
                    name="orderNumber"
                    value={orderData.orderNumber}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Généré automatiquement"
                    readOnly
                  />
                </div>
                
                <div>
                  <label htmlFor="date" className="block text-white font-medium mb-1">
                    Date <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <CalendarDaysIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type="date"
                      id="date"
                      name="date"
                      value={orderData.date}
                      onChange={handleInputChange}
                      className="w-full pl-10 p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white font-medium mb-1">
                    {orderType === 'customers' ? 'Client' : 'Fournisseur'} <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    {orderType === 'customers' ? (
                      <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50 z-10" />
                    ) : (
                      <BuildingOfficeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50 z-10" />
                    )}
                    <CustomerDropdown 
                      orderType={orderType}
                      selectedId={orderType === 'customers' ? orderData.customerId : orderData.supplierId}
                      selectedName={orderType === 'customers' ? orderData.customerName : orderData.supplierName}
                      customers={customers}
                      suppliers={suppliers}
                      onSelect={handleSelectEntity}
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="reference" className="block text-white font-medium mb-1">
                    Référence externe
                  </label>
                  <input
                    type="text"
                    id="reference"
                    name="reference"
                    value={orderData.reference}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Ex: Référence du client/fournisseur"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label htmlFor="documentType" className="block text-white font-medium mb-1">
                    Type de document
                  </label>
                  <select
                    id="documentType"
                    name="documentType"
                    value={orderData.documentType}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="bon-commande">Bon de commande</option>
                    <option value="devis">Devis</option>
                    <option value="pro-forma">Facture pro-forma</option>
                  </select>
                </div>
                
                {orderType === 'customers' ? (
                  <div>
                    <label htmlFor="paymentMethod" className="block text-white font-medium mb-1">
                      Méthode de paiement
                    </label>
                    <div className="relative">
                      <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                      <select
                        id="paymentMethod"
                        name="paymentMethod"
                        value={orderData.paymentMethod}
                        onChange={(e) => {
                          handleInputChange(e);
                          
                          // Automatically set due date based on payment method
                          const today = new Date();
                          let dueDate = new Date(today);
                          
                          switch(e.target.value) {
                            case 'especes':
                              // Cash payment - due immediately
                              break;
                            case 'cheque':
                              // Check payment - due in 15 days
                              dueDate.setDate(today.getDate() + 15);
                              break;
                            case 'virement':
                              // Bank transfer - due in 30 days
                              dueDate.setDate(today.getDate() + 30);
                              break;
                            case 'carte':
                              // Card payment - due in 7 days
                              dueDate.setDate(today.getDate() + 7);
                              break;
                            default:
                              break;
                          }
                          
                          // Only set dueDate if it's not already set by the user
                          if (!orderData.dueDate) {
                            setOrderData(prev => ({
                              ...prev,
                              dueDate: dueDate.toISOString().split('T')[0]
                            }));
                          }
                        }}
                        className="w-full pl-10 p-3 bg-white/10 border border-white/20 rounded-xl text-white appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="especes">Espèces</option>
                        <option value="cheque">Chèque</option>
                        <option value="virement">Virement bancaire</option>
                        <option value="carte">Carte bancaire</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-white/50">
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-white/50">
                      {orderData.paymentMethod === 'especes' && "Paiement immédiat, pas de délai"}
                      {orderData.paymentMethod === 'cheque' && "Délai de paiement standard: 15 jours"}
                      {orderData.paymentMethod === 'virement' && "Délai de paiement standard: 30 jours"}
                      {orderData.paymentMethod === 'carte' && "Délai de traitement: 7 jours"}
                    </p>
                  </div>
                ) : (
                  <div>
                    <label htmlFor="deliveryStatus" className="block text-white font-medium mb-1">
                      Statut de livraison
                    </label>
                    <select
                      id="deliveryStatus"
                      name="deliveryStatus"
                      value={orderData.deliveryStatus}
                      onChange={handleInputChange}
                      className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="non-expedie">Non expédié</option>
                      <option value="en-preparation">En préparation</option>
                      <option value="en-transit">En transit</option>
                      <option value="termine">Terminé</option>
                      <option value="annule">Annulé</option>
                    </select>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label htmlFor="status" className="block text-white font-medium mb-1">
                    Statut de la commande
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={orderData.status}
                    onChange={handleInputChange}
                    className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {orderType === 'customers' ? (
                      <>
                        <option value="en-attente">En attente</option>
                        <option value="en-cours">En cours</option>
                        <option value="livre">Livré</option>
                        <option value="annule">Annulé</option>
                      </>
                    ) : (
                      <>
                        <option value="en-attente">En attente</option>
                        <option value="confirme">Confirmé</option>
                        <option value="recu">Reçu</option>
                        <option value="annule">Annulé</option>
                      </>
                    )}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="dueDate" className="block text-white font-medium mb-1">
                    {orderType === 'customers' ? 'Date d\'échéance' : 'Date de livraison prévue'}
                  </label>
                  <div className="relative">
                    <CalendarDaysIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type="date"
                      id={orderType === 'customers' ? 'dueDate' : 'expectedDate'}
                      name={orderType === 'customers' ? 'dueDate' : 'expectedDate'}
                      value={orderType === 'customers' ? orderData.dueDate : orderData.expectedDate}
                      onChange={handleInputChange}
                      className="w-full pl-10 p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  {orderType === 'customers' && orderData.dueDate && (
                    <p className="mt-1 text-xs text-white/50">
                      {new Date(orderData.dueDate) > new Date() ? 
                        `Échéance dans ${Math.ceil((new Date(orderData.dueDate) - new Date()) / (1000 * 60 * 60 * 24))} jours` : 
                        new Date(orderData.dueDate).toDateString() === new Date().toDateString() ?
                          "Échéance aujourd'hui" : 
                          `Échéance dépassée de ${Math.ceil((new Date() - new Date(orderData.dueDate)) / (1000 * 60 * 60 * 24))} jours`}
                    </p>
                  )}
                </div>
              </div>
              
              {orderType === 'customers' && (
                <div className="mt-6">
                  <label htmlFor="paymentStatus" className="block text-white font-medium mb-1">
                    Statut du paiement
                  </label>
                  <div className="relative">
                    <CurrencyDollarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <select
                      id="paymentStatus"
                      name="paymentStatus"
                      value={orderData.paymentStatus}
                      onChange={handleInputChange}
                      className={`w-full pl-10 p-3 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-xl text-white border ${
                        orderData.paymentStatus === 'paid' ? 'bg-green-500/20 border-green-500/30' : 
                        orderData.paymentStatus === 'partial' ? 'bg-yellow-500/20 border-yellow-500/30' : 
                        orderData.paymentStatus === 'cancelled' ? 'bg-red-500/20 border-red-500/30' : 
                        'bg-white/10 border-white/20'
                      }`}
                    >
                      <option value="pending">En attente</option>
                      <option value="partial">Partiel</option>
                      <option value="paid">Payé</option>
                      <option value="cancelled">Annulé</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-white/50">
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-white/50">
                    {orderData.paymentStatus === 'pending' && "Le paiement n'a pas encore été reçu"}
                    {orderData.paymentStatus === 'partial' && "Une partie du paiement a été reçue"}
                    {orderData.paymentStatus === 'paid' && "Le paiement a été entièrement reçu"}
                    {orderData.paymentStatus === 'cancelled' && "Le paiement a été annulé"}
                  </p>
                </div>
              )}
            </div>

            {/* Articles */}
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-white">Articles</h2>
                
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="bg-white/10 hover:bg-white/20 transition-colors py-2 px-4 rounded-lg text-white flex items-center space-x-2"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span>Ajouter un article</span>
                </button>
              </div>
              
              {showProductSearch && (
                <div className="mb-6 bg-white/5 rounded-xl p-4 border border-white/10">
                  <div className="flex items-center mb-4">
                    <h3 className="text-white font-medium">Rechercher un produit</h3>
                    <button
                      type="button"
                      onClick={() => setShowProductSearch(false)}
                      className="ml-auto p-1.5 bg-white/10 rounded-lg hover:bg-white/20 text-white"
                    >
                      <XCircleIcon className="h-5 w-5" />
                    </button>
                  </div>
                  
                  <div className="relative mb-4">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Rechercher par nom ou référence"
                      className="w-full pl-10 p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  
                  <div className="max-h-64 overflow-y-auto bg-white/5 rounded-lg">
                    <table className="min-w-full divide-y divide-white/10">
                      <thead className="bg-white/5">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-white/70">Référence</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-white/70">Produit</th>
                          <th className="px-4 py-2 text-right text-xs font-medium text-white/70">Prix</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-white/70">Stock</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-white/70">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProducts.length > 0 ? (
                          filteredProducts.map((product) => (
                            <tr key={product.id} className="hover:bg-white/10">
                              <td className="px-4 py-2 text-sm text-white/80">{product.sku || '-'}</td>
                              <td className="px-4 py-2 text-sm text-white">{product.name || 'Produit sans nom'}</td>
                              <td className="px-4 py-2 text-sm text-white text-right">{(product.price || 0).toLocaleString()} DA</td>
                              <td className="px-4 py-2 text-sm text-white text-center">{product.stock || 0}</td>
                              <td className="px-4 py-2 text-center">
                                <button
                                  type="button"
                                  onClick={() => handleSelectProduct(product)}
                                  className="p-1.5 bg-indigo-500/20 hover:bg-indigo-500/30 rounded-lg text-indigo-400"
                                >
                                  <PlusIcon className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : searchQuery.trim() !== '' ? (
                          <tr>
                            <td colSpan="5" className="px-4 py-4 text-center text-white/60">
                              Aucun produit trouvé
                            </td>
                          </tr>
                        ) : (
                          <tr>
                            <td colSpan="5" className="px-4 py-4 text-center text-white/60">
                              Chargement des produits...
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {orderData.items.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-white/10">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white/70">Produit</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-white/70">Référence</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-white/70">Quantité</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-white/70">Prix unitaire</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-white/70">Total</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-white/70">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {orderData.items.map((item, index) => (
                        <tr key={index}>
                          <td className="px-4 py-3 text-sm text-white">{item.name}</td>
                          <td className="px-4 py-3 text-sm text-white/80">{item.sku}</td>
                          <td className="px-4 py-3 text-sm">
                            <input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                              className="w-20 p-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-center focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-white text-right">
                            <input
                              type="number"
                              min="0"
                              value={item.price}
                              onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                              className="w-28 p-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-right focus:outline-none focus:ring-1 focus:ring-indigo-500"
                            />
                            <span className="ml-1">DA</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-white font-medium text-right">{item.total.toLocaleString()} DA</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => handleRemoveItem(index)}
                              className="p-1.5 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-400 transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 border border-dashed border-white/20 rounded-xl">
                  <DocumentTextIcon className="h-12 w-12 mx-auto text-white/30 mb-2" />
                  <p className="text-white/60">Aucun article ajouté à cette commande.</p>
                  <p className="text-white/50 text-sm mt-1">Cliquez sur "Ajouter un article" pour commencer.</p>
                </div>
              )}

              {orderData.items.length > 0 && (
                <div className="mt-6 flex justify-end">
                  <div className="bg-white/5 rounded-xl p-4 w-64 space-y-3">
                    <div className="flex justify-between text-white/80">
                      <span>Total:</span>
                      <span className="font-bold text-white">{totalAmount.toLocaleString()} DA</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <h2 className="text-xl font-semibold text-white mb-4">Notes</h2>
              
              <textarea
                id="notes"
                name="notes"
                value={orderData.notes}
                onChange={handleInputChange}
                rows="4"
                className="w-full p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Notes additionnelles sur cette commande..."
              ></textarea>
            </div>
            
            {/* Boutons d'action */}
            <div className="flex justify-end space-x-4 pt-4">
              <button
                type="button"
                onClick={() => navigate('/orders')}
                className="px-6 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-colors flex items-center"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="animate-spin h-4 w-4 mr-2 border-b-2 border-white rounded-full"></span>
                    Traitement...
                  </>
                ) : isEditMode ? 'Mettre à jour' : 'Enregistrer'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default OrderForm; 